import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

// Injects raw HTML (incl. <script>) so AdSense/embed code actually executes.
function HtmlEmbed({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html || "";
    el.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      for (const attr of Array.from(old.attributes)) {
        s.setAttribute(attr.name, attr.value);
      }
      s.text = old.textContent || "";
      old.parentNode.replaceChild(s, old);
    });
  }, [html]);
  return <div ref={ref} className="w-full overflow-hidden" />;
}

function AdUnit({ ad, wide }) {
  const trackClick = () => {
    api.post(`/ads/${ad.id}/click`).catch(() => {});
  };

  const imgClass = wide
    ? "mx-auto h-auto max-h-[200px] w-auto rounded-lg"
    : "h-auto w-full rounded-lg";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60 p-2",
        wide && "mx-auto max-w-2xl",
      )}
      data-testid={`ad-unit-${ad.id}`}
    >
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        Реклама
      </span>
      {ad.type === "banner" ? (
        ad.link_url ? (
          <a
            href={ad.link_url}
            target={ad.open_new_tab ? "_blank" : "_self"}
            rel="noopener noreferrer sponsored"
            onClick={trackClick}
            data-testid={`ad-banner-link-${ad.id}`}
            className="block"
          >
            <img
              src={ad.image_url}
              alt={ad.alt || "Реклама"}
              loading="lazy"
              decoding="async"
              className={imgClass}
            />
          </a>
        ) : (
          <img
            src={ad.image_url}
            alt={ad.alt || "Реклама"}
            loading="lazy"
            decoding="async"
            className={imgClass}
          />
        )
      ) : (
        <HtmlEmbed html={ad.code} />
      )}
    </div>
  );
}

const WIDE_PLACEMENTS = ["home_top", "home_infeed", "footer"];
const ROTATE_MS = 8000;

export default function AdSlot({ placement, className }) {
  const { data } = useQuery({
    queryKey: ["ads", placement],
    queryFn: () => api.get(`/ads?placement=${placement}`).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const ads = data || [];
  const [idx, setIdx] = useState(0);
  const pausedRef = useRef(false);

  // rotate between multiple ads in the same placement
  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => {
      if (!pausedRef.current) setIdx((i) => (i + 1) % ads.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [ads.length]);

  const safeIdx = ads.length ? idx % ads.length : 0;
  const current = ads[safeIdx];

  // count an impression whenever a different ad becomes visible
  useEffect(() => {
    if (current?.id) {
      api.post(`/ads/${current.id}/impression`).catch(() => {});
    }
  }, [current?.id]);

  if (ads.length === 0) return null;
  const wide = WIDE_PLACEMENTS.includes(placement);

  return (
    <div
      className={cn("space-y-2", className)}
      data-testid={`ad-slot-${placement}`}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      <AdUnit ad={current} wide={wide} />
      {ads.length > 1 && (
        <div className="flex justify-center gap-1.5" data-testid={`ad-dots-${placement}`}>
          {ads.map((a, i) => (
            <button
              key={a.id}
              onClick={() => setIdx(i)}
              aria-label={`Реклама ${i + 1}`}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === safeIdx ? "w-4 bg-primary" : "w-1.5 bg-slate-300 hover:bg-slate-400",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
